import { useUserDirectory } from "@/hooks/use-user-directory";
import { useUser } from "@/components/UserProvider";
import { MatterLitigationDateContextMenu } from "@/components/features/matter/context-menus/MatterLitigationDateContextMenu";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  DataTable,
  ExpandableText,
  Form,
  Input,
  LoadingSpinner,
  SearchableSelectFormField,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import { BigCalendar } from "@/components/ui/BigCalendar/BigCalendar";
import { ActionConfirmationModal } from "@/components/ui/ActionConfirmationModal/ActionConfirmationModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { useHandleSearchParams } from "@/hooks/useGlobalModal";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  useDeleteMatterLitigationDate,
  useRemoveMatterLitigationDatePlanConnection,
} from "@/lib/data/react-query/matters/matter-litigation-date";
import {
  CATEGORY_TO_DUMMY_DATA_MAP,
  MATTER_LITIGATION_DATE_RESPONSIBLE_FIELD_LABEL,
  MATTER_LITIGATION_DATE_TYPE_CATEGORY_OPTIONS,
  MATTER_LITIGATION_DATE_USER_CONNECTION_LABEL,
} from "@/lib/data/static/matters/litigationStatics";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { Matter } from "@/lib/types";
import { formatDate, formatTime, formatUsersName } from "@/lib/utils/format";
import { LitigationIcon } from "@/lib/utils/iconUtils";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import Fuse from "fuse.js";
import { ChevronRight, Pencil } from "lucide-react";
import pluralize from "pluralize";
import { useEffect, useMemo, useRef, useState } from "react";
import { type Event } from "react-big-calendar";
import { useForm } from "react-hook-form";
import { litigationDateTableColumns } from "./litigation-dates-table-columns";

interface MatterLitigationProps {
  matter: Matter;
  searchLitigationDateId?: string;
}

type LitigationFilterFormValues = {
  responsibleUserFilter: string;
};

const LITIGATION_RESPONSIBLE_FILTER_ALL = "all";
const LITIGATION_RESPONSIBLE_FILTER_NONE = "none";

function getLitigationDateResponsibleConnection(matter: Matter, id: string) {
  return matter.userConnections.find(
    (c) =>
      c.litigationDateId === id &&
      c.label === MATTER_LITIGATION_DATE_USER_CONNECTION_LABEL,
  );
}

function getLitigationDateResponsibleUserId(
  matter: Matter,
  id: string,
): string | undefined {
  return (
    getLitigationDateResponsibleConnection(matter, id)?.userId ?? undefined
  );
}

function getLitigationDateResponsibleDisplay(
  matter: Matter,
  id: string,
): string | undefined {
  const connection = getLitigationDateResponsibleConnection(matter, id);
  if (!connection) return undefined;
  const name = formatUsersName(connection)?.trim();
  return name || connection.userId || undefined;
}

export const MatterLitigation = ({
  matter,
  searchLitigationDateId,
}: MatterLitigationProps) => {
  const { hasPagePermission, hasActionPermission } = useUser();
  const { openLitigationDateModal, openLitigationDatePlanConnectionModal } =
    useGlobalModal();
  const removePlanConnection = useRemoveMatterLitigationDatePlanConnection();
  const deleteLitigationDate = useDeleteMatterLitigationDate();
  const processedSearches = useRef<Set<string>>(new Set());
  const constructingTimeoutRef = useRef<number | null>(null);
  const { handleLitigationDateSearch } = useHandleSearchParams();
  useEffect(() => {
    if (!searchLitigationDateId) return;

    const searchKey = `litigationDate-${searchLitigationDateId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    handleLitigationDateSearch(matter, searchLitigationDateId);
  }, [searchLitigationDateId, matter, handleLitigationDateSearch]);
  const [isConstructingCasePlan, setIsConstructingCasePlan] = useState(false);

  useEffect(() => {
    const handleConstructingCasePlan = (
      event: CustomEvent<{ matterId: string }>,
    ) => {
      if (event.detail.matterId !== matter.id) return;

      setIsConstructingCasePlan(true);

      if (constructingTimeoutRef.current !== null) {
        window.clearTimeout(constructingTimeoutRef.current);
      }

      constructingTimeoutRef.current = window.setTimeout(() => {
        setIsConstructingCasePlan(false);
        constructingTimeoutRef.current = null;
      }, 5000);
    };

    window.addEventListener(
      "matter-litigation-case-plan-constructing",
      handleConstructingCasePlan as EventListener,
    );

    return () => {
      window.removeEventListener(
        "matter-litigation-case-plan-constructing",
        handleConstructingCasePlan as EventListener,
      );

      if (constructingTimeoutRef.current !== null) {
        window.clearTimeout(constructingTimeoutRef.current);
      }
    };
  }, [matter.id]);
  const hasSettingsPermission = Boolean(true);
  const matterSettingsQueryOptions = retrieveMatterSettingsQueryOptions();
  const { data: matterSettingsData } = useQuery({
    ...matterSettingsQueryOptions,
    enabled: hasSettingsPermission,
  });

  const litigationDates = matter.litigationDates;
  const planLinkedLitigationDates = litigationDates.filter(
    (litigationDate) => litigationDate.planId !== null,
  );
  const hasPlanConnection = planLinkedLitigationDates.length > 0;
  const hasManualLitigationDates = litigationDates.length > 0;
  const today = dayjs().startOf("day");

  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailLitigation,
  );
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDate,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDate,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDate,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canOpenExistingLitigationDate =
    hasUpdatePermission || hasDeletePermission;
  const needsInitialSelection =
    hasCreatePermission && !hasPlanConnection && !hasManualLitigationDates;

  const typeLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    matterSettingsData?.data.litigationDateTypes.forEach((type) => {
      map.set(type.id, type.label);
    });
    return map;
  }, [matterSettingsData]);

  const planLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    matterSettingsData?.data.litigationDatePlans.forEach((plan) => {
      map.set(plan.id, plan.label);
    });
    return map;
  }, [matterSettingsData]);

  const typeCategoryMap = useMemo(() => {
    const map = new Map<string, string>();
    matterSettingsData?.data.litigationDateTypes.forEach((type) => {
      const category = type.category || "Filing & Service";

      // safe guard against unknown categories
      map.set(type.id, category);
    });
    return map;
  }, [matterSettingsData]);

  const [query, setQuery] = useState("");
  const [responsibleUserFilter, setResponsibleUserFilter] = useState(
    LITIGATION_RESPONSIBLE_FILTER_ALL,
  );
  const filterForm = useForm<LitigationFilterFormValues>({
    values: {
      responsibleUserFilter,
    },
  });
  const [activeTab, setActiveTab] = useState("Chronology");
  const [isDeletePlanModalOpen, setIsDeletePlanModalOpen] = useState(false);
  const sanitizedQuery = query.trim();
  const hasActiveLitigationFilters =
    sanitizedQuery.length > 0 ||
    responsibleUserFilter !== LITIGATION_RESPONSIBLE_FILTER_ALL;

  const showLitigationToolbar = !needsInitialSelection;

  const userDirectory = useUserDirectory({
    enabled: showLitigationToolbar,
    selectedIds: [responsibleUserFilter].filter(
      (id) =>
        ![
          LITIGATION_RESPONSIBLE_FILTER_ALL,
          LITIGATION_RESPONSIBLE_FILTER_NONE,
        ].includes(id),
    ),
  });
  const usersSortedForFilter = userDirectory.users;
  const isUsersLoading = userDirectory.isLoading;
  const responsibleUserFilterOptions = useMemo(() => {
    return [
      {
        value: LITIGATION_RESPONSIBLE_FILTER_ALL,
        label: "All",
      },
      {
        value: LITIGATION_RESPONSIBLE_FILTER_NONE,
        label: "Not assigned",
      },
      ...usersSortedForFilter.map((user) => ({
        value: user.id,
        label: formatUsersName(user)?.trim() || user.id,
      })),
    ];
  }, [usersSortedForFilter]);

  const filteredLitigationDates = useMemo(() => {
    let dates = litigationDates;

    if (responsibleUserFilter === LITIGATION_RESPONSIBLE_FILTER_NONE) {
      dates = dates.filter(
        (d) => !getLitigationDateResponsibleUserId(matter, d.id),
      );
    } else if (responsibleUserFilter !== LITIGATION_RESPONSIBLE_FILTER_ALL) {
      dates = dates.filter(
        (d) =>
          getLitigationDateResponsibleUserId(matter, d.id) ===
          responsibleUserFilter,
      );
    }

    if (!sanitizedQuery) return dates;

    const searchable = dates.map((litigationDate) => ({
      ...litigationDate,
      typeLabel:
        typeLabelMap.get(litigationDate.typeId) ?? litigationDate.label ?? "",
      responsibleDisplay:
        getLitigationDateResponsibleDisplay(matter, litigationDate.id) ?? "",
    }));
    const fuse = new Fuse(searchable, {
      keys: [
        { name: "typeLabel", weight: 0.7 },
        { name: "description", weight: 0.2 },
        { name: "responsibleDisplay", weight: 0.1 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      includeScore: false,
      useExtendedSearch: false,
    });
    return fuse.search(sanitizedQuery).map((result) => result.item);
  }, [
    litigationDates,
    matter,
    responsibleUserFilter,
    sanitizedQuery,
    typeLabelMap,
  ]);

  type LitigationDate = (typeof litigationDates)[number];

  const categorized = filteredLitigationDates.reduce<{
    upcoming: LitigationDate[];
    past: LitigationDate[];
  }>(
    (acc, litigationDate) => {
      const completed = litigationDate.completedDate;
      const deadline = litigationDate.deadlineDate;

      if (completed) {
        acc.past.push(litigationDate);
        return acc;
      }

      if (!deadline) {
        acc.past.push(litigationDate);
        return acc;
      }

      const deadlineDate = dayjs(deadline);
      if (deadlineDate.isBefore(today)) {
        acc.past.push(litigationDate);
        return acc;
      }

      acc.upcoming.push(litigationDate);
      return acc;
    },
    {
      upcoming: [],
      past: [],
    },
  );

  const sortedUpcoming = [...categorized.upcoming].sort((a, b) => {
    if (!a.deadlineDate) return 1;
    if (!b.deadlineDate) return -1;
    return dayjs(a.deadlineDate).diff(dayjs(b.deadlineDate));
  });

  const sortedPast = [...categorized.past].sort((a, b) => {
    const aDate = a.completedDate;
    const bDate = b.completedDate;

    if (!aDate) return 1;
    if (!bDate) return -1;

    return dayjs(bDate).diff(dayjs(aDate));
  });

  const formatLitigationDate = (value?: string | null) => {
    if (!value) return undefined;
    return formatDate(value);
  };

  const formatLitigationDateWithTime = (
    date?: string | null,
    time?: string | null,
  ): string | undefined => {
    const dateLabel = formatLitigationDate(date);
    const timeLabel = formatTime(time ?? undefined);

    if (!dateLabel && !timeLabel) return undefined;
    if (!dateLabel) return timeLabel;
    if (!timeLabel) return dateLabel;

    return `${dateLabel} • ${timeLabel}`;
  };

  const renderLitigationDateCard = (
    litigationDate: (typeof litigationDates)[number],
  ) => {
    const deadline = formatLitigationDateWithTime(
      litigationDate.deadlineDate,
      litigationDate.deadlineTime,
    );
    const completed = formatLitigationDate(litigationDate.completedDate);
    const typeLabel =
      typeLabelMap.get(litigationDate.typeId) ?? litigationDate.label ?? "";
    const category =
      litigationDate.typeCategory ??
      typeCategoryMap.get(litigationDate.typeId) ??
      "Other";
    const planLabel = litigationDate.planId
      ? planLabelMap.get(litigationDate.planId)
      : undefined;
    const userDisplay = getLitigationDateResponsibleDisplay(
      matter,
      litigationDate.id,
    );

    const handleEdit = () => {
      openLitigationDateModal({
        matterId: matter.id,
        litigationDateId: litigationDate.id,
        editing: true,
        initialTab: "details",
      });
    };

    return (
      <MatterLitigationDateContextMenu
        canDelete={hasDeletePermission}
        completed={completed}
        deadline={deadline}
        description={litigationDate.description}
        id={litigationDate.id}
        isDeleting={deleteLitigationDate.isPending}
        label={typeLabel}
        matter={matter}
        onEdit={handleEdit}
        onDelete={async () => {
          await deleteLitigationDate.mutateAsync({
            matterId: matter.id,
            id: litigationDate.id,
          });
        }}
        trigger={
          <Card className="border-border hover:bg-muted/50 group w-full cursor-pointer transition-colors">
            <div className="flex items-start justify-between p-6 pb-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Text variant="cardTitle" className="mb-0 break-all">
                    {typeLabel}
                  </Text>
                  <Badge
                    variant={
                      litigationDate.confirmation ? "success" : "warning"
                    }
                  >
                    {litigationDate.confirmation
                      ? "Confirmed"
                      : "Awaiting confirmation"}
                  </Badge>
                  {planLabel && (
                    <Badge
                      variant="secondary"
                      className="bg-primary/10 text-primary border-primary/30 max-w-[260px] truncate tracking-wide uppercase"
                    >
                      {`Plan: ${planLabel}`}
                    </Badge>
                  )}
                </div>
                <div className="mt-2 flex flex-col gap-y-1">
                  <div className="flex items-center gap-x-1">
                    <Text variant="subHeading" className="self-start">
                      Deadline:
                    </Text>
                    <Text variant="body" className="font-semibold">
                      {deadline ?? "No deadline set"}
                    </Text>
                  </div>
                  <div className="flex items-center gap-x-1">
                    <Text variant="subHeading" className="self-start">
                      Completed:
                    </Text>
                    <Text variant="body" className="font-semibold">
                      {completed ?? "Not completed"}
                    </Text>
                  </div>
                  <div className="flex items-center gap-x-1">
                    <Text variant="subHeading" className="self-start">
                      Category:
                    </Text>
                    <Text variant="body" className="font-semibold">
                      {category}
                    </Text>
                  </div>
                  {userDisplay ? (
                    <div className="flex items-center gap-x-1">
                      <Text variant="subHeading" className="self-start">
                        {`${MATTER_LITIGATION_DATE_RESPONSIBLE_FIELD_LABEL}:`}
                      </Text>
                      <Text variant="body" className="font-semibold">
                        {userDisplay}
                      </Text>
                    </div>
                  ) : null}
                </div>
              </div>
              {canOpenExistingLitigationDate && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "text-muted-foreground hover:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 h-8 w-8 shrink-0",
                    "opacity-100 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100",
                  )}
                  onClick={() => {
                    openLitigationDateModal({
                      matterId: matter.id,
                      litigationDateId: litigationDate.id,
                      editing: true,
                      initialTab: "details",
                    });
                  }}
                  data-testid="edit-litigation-date-button"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
            {litigationDate.description ? (
              <CardContent className="px-6 pt-0 pb-4">
                <ExpandableText
                  text={litigationDate.description}
                  maxLines={2}
                  className="text-muted-foreground"
                />
              </CardContent>
            ) : null}
          </Card>
        }
      />
    );
  };

  const renderScrollableContent = () => {
    return (
      <div className="mt-2 flex flex-1 flex-col gap-6 overflow-auto pr-2">
        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <LitigationIcon className="text-primary h-4 w-4" />
            <Text variant="body" className="text-primary text-lg font-semibold">
              Upcoming litigation dates
            </Text>
            <Text variant="subHeading" as="span">
              {`• ${pluralize("item", sortedUpcoming.length, true)}`}
            </Text>
          </div>
          <div className="flex flex-col gap-y-4">
            {sortedUpcoming.length === 0 ? (
              <Card className="w-full border-2 border-dashed">
                <CardHeader className="space-y-1">
                  <Text variant="subHeading" className="text-center">
                    No upcoming litigation dates
                    {hasActiveLitigationFilters ? " found" : ""}
                  </Text>
                </CardHeader>
              </Card>
            ) : (
              sortedUpcoming.map(renderLitigationDateCard)
            )}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <LitigationIcon className="text-muted-foreground h-4 w-4" />
            <Text variant="body" className="text-lg font-semibold">
              Past litigation dates
            </Text>
            <Text variant="subHeading" as="span">
              {`• ${pluralize("item", sortedPast.length, true)}`}
            </Text>
          </div>
          <div className="flex flex-col gap-y-4">
            {sortedPast.length === 0 ? (
              <Card className="w-full border-2 border-dashed">
                <CardHeader className="space-y-1">
                  <Text variant="subHeading" className="text-center">
                    No past litigation dates
                    {hasActiveLitigationFilters ? " found" : ""}
                  </Text>
                </CardHeader>
              </Card>
            ) : (
              sortedPast.map(renderLitigationDateCard)
            )}
          </div>
        </section>
      </div>
    );
  };

  const litigationDatesByCategory = useMemo(() => {
    const grouped = new Map<string, LitigationDate[]>();

    filteredLitigationDates.forEach((litigationDate) => {
      const category =
        litigationDate.typeCategory ??
        typeCategoryMap.get(litigationDate.typeId) ??
        "Filing & Service";
      const existingDates = grouped.get(category);
      if (existingDates) {
        existingDates.push(litigationDate);
      } else {
        grouped.set(category, [litigationDate]);
      }
    });

    // Sort each category's dates by deadline date (or completed date if available)
    grouped.forEach((dates) => {
      dates.sort((a, b) => {
        const aDate = a.completedDate || a.deadlineDate;
        const bDate = b.completedDate || b.deadlineDate;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return dayjs(aDate).diff(dayjs(bDate));
      });
    });

    return grouped;
  }, [filteredLitigationDates, typeCategoryMap]);

  const renderCategoriesContent = () => {
    const categories = [...MATTER_LITIGATION_DATE_TYPE_CATEGORY_OPTIONS];

    return (
      <div className="mt-2 flex flex-1 flex-col gap-4 overflow-auto pr-2">
        <Accordion type="multiple" className="w-full">
          {categories.map((category) => {
            const dates = litigationDatesByCategory.get(category) ?? [];
            const categoryData = CATEGORY_TO_DUMMY_DATA_MAP[category];
            const tableData = dates.map((date) => ({
              ...date,
              litigationDateTypeLabel:
                typeLabelMap.get(date.typeId) ?? date.label ?? "",
              litigationDateUserLabel:
                getLitigationDateResponsibleDisplay(matter, date.id) ?? "",
            }));
            return (
              <AccordionItem
                key={category}
                value={category}
                className="bg-card border-border mb-2 rounded-lg border shadow-sm last:mb-0"
              >
                <div className="hover:bg-light-primary/50 flex w-full cursor-pointer items-center transition-colors">
                  <AccordionTrigger
                    className="min-w-0 flex-1 cursor-pointer px-4 py-3 hover:bg-transparent hover:no-underline [&[data-state=open]_.litigation-category-chevron]:rotate-90"
                    icon={<></>}
                  >
                    <div className="flex items-center gap-3 pr-2 text-left">
                      <ChevronRight
                        className="litigation-category-chevron text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200"
                        aria-hidden
                      />
                      <LitigationIcon className="text-primary h-4 w-4 shrink-0" />
                      <Text
                        variant="body"
                        className="text-primary font-semibold"
                      >
                        {category}
                      </Text>
                      <Text variant="subHeading" as="span">
                        {`• ${pluralize("item", dates.length, true)}`}
                      </Text>
                    </div>
                  </AccordionTrigger>
                </div>
                <AccordionContent className="px-4 pb-4">
                  {categoryData.length > 0 && (
                    <div className="mb-3 p-4">
                      <div className="grid gap-3 sm:grid-cols-3">
                        {categoryData.map(({ dataType, dataValue }) => (
                          <div key={dataType} className="space-y-1">
                            <Text
                              variant="caption"
                              color="muted"
                              className="font-medium tracking-wider uppercase"
                            >
                              {dataType}
                            </Text>
                            <Text variant="body" className="font-medium">
                              {dataValue}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="pt-2">
                    <DataTable
                      entityLabelPlural="litigation dates"
                      hideSearchBar
                      hideVisiibilityDropdown
                      allowLocalStorage={false}
                      noResultsMessage="No litigation dates in this category."
                      columns={litigationDateTableColumns}
                      data={tableData}
                      tableName={tanstackTableNames.matterLitigationDates}
                      onRowClick={(row) => {
                        if (!canOpenExistingLitigationDate) {
                          return;
                        }
                        openLitigationDateModal({
                          matterId: matter.id,
                          litigationDateId: row.original.id,
                          editing: true,
                          initialTab: "details",
                        });
                      }}
                      renderRowContextMenu={(row, trigger) => {
                        const date = row.original;
                        const label =
                          date.litigationDateTypeLabel || "litigation date";
                        return (
                          <MatterLitigationDateContextMenu
                            canDelete={hasDeletePermission}
                            deadline={date.deadlineDate}
                            description={date.description}
                            id={date.id}
                            isDeleting={deleteLitigationDate.isPending}
                            label={label}
                            matter={matter}
                            onEdit={() => {
                              openLitigationDateModal({
                                matterId: matter.id,
                                litigationDateId: date.id,
                                editing: true,
                                initialTab: "details",
                              });
                            }}
                            onDelete={async () => {
                              await deleteLitigationDate.mutateAsync({
                                matterId: matter.id,
                                id: date.id,
                              });
                            }}
                            trigger={trigger}
                          />
                        );
                      }}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    );
  };

  const calendarEvents = useMemo(() => {
    return filteredLitigationDates
      .map((ld) => {
        const deadlineDate = ld.deadlineDate;
        if (!deadlineDate) return null;

        const deadlineTime = ld.deadlineTime;

        // If we have a time, create a timed event; otherwise, treat as all-day.
        let start: Date;
        let end: Date;
        let allDay = false;

        if (deadlineTime) {
          const dateTimeString = `${deadlineDate} ${deadlineTime}`;
          const parsedStart = dayjs(dateTimeString, "YYYY-MM-DD HH:mm");

          if (!parsedStart.isValid()) {
            // Fallback to all-day if the combined value is not valid
            start = dayjs(deadlineDate).startOf("day").toDate();
            end = dayjs(deadlineDate).add(1, "day").startOf("day").toDate();
            allDay = true;
          } else {
            start = parsedStart.toDate();
            // Instant deadline: zero-duration event
            end = parsedStart.toDate();
          }
        } else {
          // Date-only: all-day event from start of day to start of next day
          start = dayjs(deadlineDate).startOf("day").toDate();
          end = dayjs(deadlineDate).add(1, "day").startOf("day").toDate();
          allDay = true;
        }

        const typeLabel = typeLabelMap.get(ld.typeId) ?? ld.label ?? "";

        return {
          start,
          end,
          title: typeLabel,
          allDay,
          resource: {
            litigationDateId: ld.id,
            description: ld.description,
            completed: ld.completedDate,
            confirmed: ld.confirmation,
          },
        };
      })
      .filter((event): event is NonNullable<typeof event> => event !== null);
  }, [filteredLitigationDates, typeLabelMap]);

  const handleEventSelect = (event: Event) => {
    const resource = event.resource;
    const litigationDateId = resource?.litigationDateId;
    if (litigationDateId) {
      openLitigationDateModal({
        matterId: matter.id,
        litigationDateId,
        editing: true,
        initialTab: "details",
      });
    }
  };

  const eventPropGetter = (event: Event) => {
    const resource = event.resource;
    const isCompleted = !!resource?.completed;

    return {
      style: {
        backgroundColor: isCompleted ? "#10b981" : "#3b82f6", // green for completed, blue for pending
        borderColor: isCompleted ? "#059669" : "#2563eb",
        color: "#ffffff",
      },
    };
  };

  const renderCalendarContent = () => {
    if (calendarEvents.length === 0) {
      return (
        <div className="bg-card flex flex-1 items-center justify-center overflow-hidden rounded-lg border border-dashed p-6 text-center shadow-sm">
          <div className="space-y-2">
            <Text variant="subHeading">
              No calendar events{hasActiveLitigationFilters ? " found" : ""}
            </Text>
            <Text variant="subHeading">
              {hasActiveLitigationFilters
                ? "Adjust your search or responsible filter to view matching events."
                : "Calendar entries appear here once litigation dates include deadlines."}
            </Text>
          </div>
        </div>
      );
    }

    return (
      <BigCalendar
        events={calendarEvents}
        onSelectEvent={handleEventSelect}
        eventPropGetter={eventPropGetter}
      />
    );
  };

  const handleRemovePlanConnection = () => {
    if (!hasPlanConnection) return;
    removePlanConnection.mutate({
      matterId: matter.id,
      litigationDates: planLinkedLitigationDates.map(
        ({ dateModified, id }) => ({ dateModified, id }),
      ),
    });
  };

  const renderInitialSetupOptions = () => {
    if (!needsInitialSelection) {
      return null;
    }

    const planOptionDisabled = !hasSettingsPermission;

    return (
      <div className="bg-card rounded-lg border border-dashed p-6 shadow-sm">
        <Text variant="body" className="text-lg font-semibold">
          How would you like to start setting up your litigation dates?
        </Text>
        <Text variant="subHeading" className="mt-1">
          Apply a plan to automatically map deadlines, create them manually, or
          both.
        </Text>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <button
            type="button"
            className={cn(
              "flex h-full flex-col rounded-lg border-2 p-5 text-left transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              planOptionDisabled
                ? "border-border bg-muted text-muted-foreground cursor-not-allowed focus-visible:ring-transparent"
                : "border-primary/60 bg-primary/5 hover:border-primary hover:bg-primary/10 focus-visible:ring-primary",
            )}
            onClick={() => {
              if (planOptionDisabled) return;
              openLitigationDatePlanConnectionModal({
                matterId: matter.id,
              });
            }}
            disabled={planOptionDisabled}
          >
            <Badge
              variant="secondary"
              className="bg-primary/15 text-primary w-fit border-none"
            >
              Recommended
            </Badge>
            <Text variant="cardTitle" color="primary" className="mt-3">
              Start by applying a litigation date plan
            </Text>
            <Text variant="subHeading" className="mt-2">
              Use one of your firm&apos;s existing litigation date plans to
              generate several key dates at once.
            </Text>
          </button>
          <button
            type="button"
            className="hover:bg-accent/70 focus-visible:ring-primary flex h-full flex-col rounded-lg border-2 border-dashed p-5 text-left transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            onClick={() => {
              openLitigationDateModal({
                matterId: matter.id,
                editing: false,
                initialTab: "details",
              });
            }}
            data-testid="create-manual-litigation-date-button"
          >
            <Text variant="cardTitle" className="mt-0">
              Start by creating a litigation date manually
            </Text>
            <Text variant="subHeading" className="mt-2">
              You can still add more dates or apply a plan at any time
            </Text>
          </button>
        </div>
        {planOptionDisabled && (
          <Text variant="caption" color="muted" className="mt-2">
            You do not have permission to connect litigation date plans.
          </Text>
        )}
      </div>
    );
  };

  if (!hasViewPermission) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col gap-y-4 p-6">
        <Text variant="title">Litigation</Text>
        <Text variant="subHeading">
          You do not have permission to view litigation dates.
        </Text>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-y-4 p-6">
      <Text variant="title">Litigation ({litigationDates.length})</Text>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
        {isConstructingCasePlan ? (
          <div className="flex h-full items-center justify-center">
            <div className="bg-background/80 max-w-md rounded-lg border p-8 text-center shadow-sm">
              <Text variant="subHeading" className="mb-2">
                Constructing case plan
              </Text>
              <Text variant="subHeading" className="mb-2">
                We&apos;re generating your litigation dates from the selected
                plan. This usually takes just a few seconds.
              </Text>
              <LoadingSpinner className="justify-self-center" />
            </div>
          </div>
        ) : (
          <>
            {!needsInitialSelection && (
              <div className="sticky top-0 z-10 flex w-full flex-wrap items-center justify-between gap-4 pb-4">
                <div className="flex max-w-full min-w-0 flex-1 flex-wrap items-center gap-3">
                  <div className="max-w-md min-w-[200px] grow">
                    <Input
                      value={query}
                      onChange={(e) => {
                        setQuery(e.target.value);
                      }}
                      placeholder="Search litigation dates"
                    />
                  </div>
                  <Form {...filterForm}>
                    <SearchableSelectFormField
                      register={filterForm.register("responsibleUserFilter")}
                      items={responsibleUserFilterOptions}
                      {...userDirectory.selectProps}
                      itemsKey="value"
                      placeholder={`${MATTER_LITIGATION_DATE_RESPONSIBLE_FIELD_LABEL}...`}
                      emptyMessage="No team members found."
                      formatItemLabel={(item) => item?.label}
                      formatSelectedItemLabel={(item) => item?.label}
                      className="w-[min(100%,280px)] min-w-[200px] shrink-0"
                      buttonClassName="bg-background hover:bg-accent hover:text-accent-foreground w-full"
                      isItemsLoading={isUsersLoading}
                      disabled={isUsersLoading}
                      onChange={setResponsibleUserFilter}
                    />
                  </Form>
                </div>
                {hasCreatePermission && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="default"
                      onClick={() => {
                        openLitigationDateModal({
                          matterId: matter.id,
                          editing: false,
                          initialTab: "details",
                        });
                      }}
                    >
                      Create new date
                    </Button>
                    {!hasPlanConnection && hasSettingsPermission && (
                      <Button
                        type="button"
                        variant="default"
                        onClick={() => {
                          openLitigationDatePlanConnectionModal({
                            matterId: matter.id,
                          });
                        }}
                      >
                        Apply new date plan
                      </Button>
                    )}
                    {hasPlanConnection && hasUpdatePermission && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive hover:bg-transparent"
                        disabled={removePlanConnection.isPending}
                        onClick={() => {
                          setIsDeletePlanModalOpen(true);
                        }}
                      >
                        Remove plan connection
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {renderInitialSetupOptions()}

            {!needsInitialSelection && (
              <Tabs
                value={activeTab}
                onValueChange={(value) => {
                  setActiveTab(value);
                }}
                className="flex flex-1 flex-col overflow-hidden"
              >
                <TabsList className="grid w-fit grid-cols-3">
                  <TabsTrigger value="Chronology">Chronology</TabsTrigger>
                  <TabsTrigger value="categories">Categories</TabsTrigger>
                  <TabsTrigger value="calendar">Calendar</TabsTrigger>
                </TabsList>

                <TabsContent
                  value="Chronology"
                  className="mt-2 flex flex-1 flex-col overflow-hidden"
                >
                  {renderScrollableContent()}
                </TabsContent>

                <TabsContent
                  value="categories"
                  className="mt-2 flex flex-1 flex-col overflow-hidden"
                >
                  {renderCategoriesContent()}
                </TabsContent>

                <TabsContent
                  value="calendar"
                  className="mt-2 flex flex-1 flex-col overflow-hidden"
                >
                  {renderCalendarContent()}
                </TabsContent>
              </Tabs>
            )}
          </>
        )}
      </div>
      <ActionConfirmationModal
        open={isDeletePlanModalOpen}
        setOpen={setIsDeletePlanModalOpen}
        title="Delete plan connection"
        description="This matter will no longer be linked to its litigation date plan. Existing litigation dates will remain."
        buttonText="Delete plan"
        afterActionFn={() => {
          setIsDeletePlanModalOpen(false);
          handleRemovePlanConnection();
        }}
        cancelText="Keep plan"
      />
    </div>
  );
};
